import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui';
import { type ProgramImportBatch, programImportService } from '../services/programImportService';

function issueText(issue: { code: string; value: string }) {
  if (issue.code === 'duplicate_poster_number') return `Duplicate poster number ${issue.value}`;
  return issue.code.replaceAll('_', ' ');
}

export function ProgramImportPanel({ eventId, onCommitted }: { eventId: string; onCommitted?: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<ProgramImportBatch | null>(null);
  const [skippedRows, setSkippedRows] = useState<number[]>([]);
  const [keptSourceKeys, setKeptSourceKeys] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    programImportService.latest(eventId).then((value) => { if (active && value) setBatch(value); }).catch(() => undefined);
    return () => { active = false; };
  }, [eventId]);

  async function review() {
    if (!file) return;
    setBusy(true);
    try {
      setBatch(await programImportService.preview(eventId, file));
      setSkippedRows([]);
      setKeptSourceKeys([]);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to review schedule');
    } finally { setBusy(false); }
  }

  async function commit() {
    if (!batch) return;
    setBusy(true);
    try {
      setBatch(await programImportService.commit(eventId, batch.id, skippedRows, keptSourceKeys));
      onCommitted?.();
      toast.success('Program imported. The schedule remains editable in the session list.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to import schedule');
    } finally { setBusy(false); }
  }

  const unresolved = batch?.issues.filter((issue) => !skippedRows.includes(issue.row)) ?? [];
  const unresolvedConflicts = batch?.conflicts?.filter((conflict) => !keptSourceKeys.includes(conflict.source_key)) ?? [];
  return <section className="mb-6 rounded-xl border border-gray-800 bg-gray-900 p-5">
    <h2 className="text-base text-white">Import schedule</h2>
    <p className="mt-1 text-sm text-gray-400">Review workbook results first. Import only occurs after an explicit commit.</p>
    <div className="mt-4 flex flex-wrap items-center gap-3">
      <label className="text-sm text-gray-300">Schedule workbook<input aria-label="Schedule workbook" className="ml-3 text-xs" type="file" accept=".xlsx" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></label>
      <Button onClick={review} disabled={!file || busy} loading={busy}>Review schedule</Button>
    </div>
    {batch && <div className="mt-4 rounded-lg bg-gray-800/70 p-4 text-sm">
      <p className="text-gray-200"><strong>{batch.summary.sessions ?? batch.sessions.length} sessions</strong> · {batch.summary.items ?? 0} presentations</p>
      {batch.suggested_interests.length > 0 && <p className="mt-2 text-xs text-gray-400">Suggested event interests: {batch.suggested_interests.join(', ')}</p>}
      {batch.issues.map((issue) => <div key={`${issue.sheet}-${issue.row}-${issue.code}`} className="mt-3 flex flex-wrap items-center gap-3 rounded bg-amber-500/10 px-3 py-2 text-amber-200">
        <span>{issueText(issue)} — {issue.sheet}, row {issue.row}</span>
        {skippedRows.includes(issue.row)
          ? <span className="text-xs text-amber-100">Explicitly skipped</span>
          : <Button size="sm" variant="secondary" onClick={() => setSkippedRows((rows) => [...rows, issue.row])}>Skip row {issue.row}</Button>}
      </div>)}
      {batch.conflicts?.map((conflict) => <div key={conflict.source_key} className="mt-3 flex flex-wrap items-center gap-3 rounded bg-red-500/10 px-3 py-2 text-red-200">
        <span>Manual {conflict.entity} edit: “{conflict.current.title}” differs from workbook “{conflict.proposed.title}”.</span>
        {keptSourceKeys.includes(conflict.source_key)
          ? <span className="text-xs text-red-100">Keeping organizer edit</span>
          : <Button size="sm" variant="secondary" onClick={() => setKeptSourceKeys((keys) => [...keys, conflict.source_key])}>Keep organizer edit</Button>}
      </div>)}
      {batch.state === 'validated' && <Button className="mt-4" onClick={commit} loading={busy} disabled={unresolved.length > 0 || unresolvedConflicts.length > 0}>Commit program</Button>}
      {batch.state === 'confirmed' && <p className="mt-4 text-emerald-300">Program imported. You can now review or edit individual sessions below.</p>}
    </div>}
  </section>;
}
